import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PencilIcon, XIcon, ReplyIcon, DownloadIcon, UploadIcon } from '@primer/octicons-react';
import axios from 'axios';
import config from '../config';
import Bookmark from './Bookmark';
import AddBookmark from './AddBookmark';
import ModalForm from './ModalForm';
import ModalShare from './ModalShare';
import ModalImport from './ModalImport';

function BookmarkCollection() {
    const { id } = useParams();
    const [bookmarks, setBookmarks] = useState({});
    const [hasAccess, setHasAccess] = useState(false);
    const [isOwner, setIsOwner] = useState(false);
    const [collectionUuid, setCollectionUuid] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [btnEditText, setBtnEditText] = useState('Edit');
    const [editIcon, setEditIcon] = useState('pencil');
    const [editTitle, setEditTitle] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [readonlyEnabled, setReadonlyEnabled] = useState(false);

    console.log('BookmarkCollection State:', { hasAccess, bookmarks, collectionUuid });

    // Modal states
    const [showAddBookmarkModal, setShowAddBookmarkModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [addBookmarkMessage, setAddBookmarkMessage] = useState('');
    const [passwordMessage, setPasswordMessage] = useState('');

    const addBookmarkFormFields = [
        {
            label: 'Bookmark Title:',
            placeholder: 'Title',
            inputType: 'search',
            name: 'addBookmarkTitle',
            id: 'title',
            optional: false,
            maxlength: 50,
        },
        {
            label: 'Bookmark Url:',
            placeholder: 'Url',
            inputType: 'search',
            name: 'addBookmarkUrl',
            id: 'url',
            optional: false,
            value: 'https://'
        }
    ];

    const passwordFormFields = [
        {
            label: 'Enter password',
            placeholder: 'Password',
            inputType: 'password',
            name: 'password',
            id: 'password',
            optional: false,
            maxlength: 30,
            value: ''
        }
    ];

    useEffect(() => {
        getBookmarks('');
    }, [id]);

    const getBookmarks = (password) => {
        const path = config.serverPath + '/api/bookmarks';
        const payload = {
            collection_uuid: id,
            password: password
        };

        console.log('Calling API:', path, 'with payload:', payload);
        axios.post(path, payload)
            .then((res) => {
                console.log('API Response:', res.data);
                if (res.data.status === 'success') {
                    setBookmarks(res.data.bookmarks);
                    setReadonlyEnabled(res.data.bookmarks.readonly_enabled || false);
                    setHasAccess(true);
                    setIsOwner(res.data.is_owner || false);
                    setCollectionUuid(id);
                    setEditTitle(res.data.bookmarks.title);
                } else if (res.data.message === 'Password required' || res.data.message === 'Wrong password') {
                    if (res.data.message === 'Wrong password') {
                        setPasswordMessage(res.data.message);
                    }
                    setShowPasswordModal(true);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const submitAddForm = (formData) => {
        const payload = {
            ...formData,
            collection_uuid: id
        };

        axios.post(config.serverPath + '/api/addbookmark', payload)
            .then((res) => {
                if (res.data.status === 'success') {
                    setShowAddBookmarkModal(false);
                    setAddBookmarkMessage('');
                    getBookmarks('');
                } else {
                    setAddBookmarkMessage(res.data.message);
                }
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const submitPasswordForm = (formData) => {
        setShowPasswordModal(false);
        getBookmarks(formData['password']);
    };

    const toggleEdit = () => {
        const newEditMode = !editMode;
        setEditMode(newEditMode);

        if (!newEditMode) {
            setBtnEditText('Edit');
            setEditIcon('pencil');
        } else {
            setBtnEditText('Cancel');
            setEditIcon('x');
        }
    };

    const showExportDialog = () => {
        const format = window.confirm('Export as Markdown? (Cancel for HTML)') ? 'markdown' : 'html';
        exportBookmarks(format);
    };

    const exportBookmarks = (format) => {
        const path = config.serverPath + '/api/exportbookmarks';
        const params = {
            collection_uuid: collectionUuid,
            format: format
        };

        axios.get(path, { params })
            .then((res) => {
                if (res.data.status === 'fail') {
                    console.error(res.data.message);
                    return;
                }

                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;

                const ext = format === 'markdown' ? '.md' : '.' + format;
                link.setAttribute('download', bookmarks.title + ext);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const submitEditTitle = () => {
        const path = config.serverPath + '/api/editcollectiontitle';
        const payload = {
            collection_uuid: collectionUuid,
            title: editTitle
        };

        axios.post(path, payload)
            .then((res) => {
                if (res.data.status === 'success') {
                    if (editMode) {
                        toggleEdit();
                    }
                    getBookmarks('');
                } else {
                    setErrorMessage(res.data.message);
                }
            })
            .catch((error) => {
                console.log(error);
            });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
            <div className="max-w-7xl mx-auto">
                {/* Title Section */}
                {hasAccess && !editMode && (
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 text-center mb-12">
                        {bookmarks.title}
                    </h1>
                )}
                {hasAccess && editMode && (
                    <div className="text-center mb-12">
                        <input
                            className="text-4xl md:text-5xl font-bold text-gray-900 text-center bg-transparent border-b-2 border-blue-500 focus:outline-none focus:border-blue-600 px-4 py-2 mb-4"
                            type="search"
                            maxLength={50}
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && submitEditTitle()}
                        />
                        <div className="mt-4">
                            <button
                                onClick={submitEditTitle}
                                className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-md"
                            >
                                Save Title
                            </button>
                        </div>
                        {errorMessage && (
                            <div className="mt-4 text-red-600 font-medium">{errorMessage}</div>
                        )}
                    </div>
                )}

                {/* Bookmarks Grid */}
                {hasAccess ? (
                    <div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 mb-8">
                            {bookmarks.bookmarks && bookmarks.bookmarks.map((bookmark, index) => (
                                <Bookmark
                                    key={index}
                                    url={bookmark.url}
                                    title={bookmark.title}
                                    bookmarkId={bookmark.id}
                                    editMode={editMode && isOwner}
                                />
                            ))}
                            {console.log('Rendering AddBookmark button, hasAccess:', hasAccess, 'isOwner:', isOwner, 'bookmarks:', bookmarks)}
                            {isOwner && <AddBookmark clicked={() => setShowAddBookmarkModal(true)} />}
                        </div>

                        {/* Control Buttons */}
                        {isOwner && (
                            <div className="flex flex-wrap justify-center gap-4 mt-12">
                                <button
                                    onClick={toggleEdit}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-700 font-semibold rounded-xl shadow-md hover:shadow-lg transition-all hover:scale-105"
                                >
                                    {editIcon === 'pencil' ? <PencilIcon size={18} /> : <XIcon size={18} />}
                                    <span>{btnEditText}</span>
                                </button>

                                <button
                                    onClick={() => setShowShareModal(true)}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-700 font-semibold rounded-xl shadow-md hover:shadow-lg transition-all hover:scale-105"
                                >
                                    <ReplyIcon size={18} />
                                    <span>Share</span>
                                </button>

                                <button
                                    onClick={showExportDialog}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-700 font-semibold rounded-xl shadow-md hover:shadow-lg transition-all hover:scale-105"
                                >
                                    <DownloadIcon size={18} />
                                    <span>Export</span>
                                </button>

                                <button
                                    onClick={() => setShowImportModal(true)}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-700 font-semibold rounded-xl shadow-md hover:shadow-lg transition-all hover:scale-105"
                                >
                                    <UploadIcon size={18} />
                                    <span>Import</span>
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-gray-500">Loading collection... (hasAccess: {hasAccess.toString()})</p>
                    </div>
                )}

                <ModalForm
                    isOpen={showAddBookmarkModal}
                    onClose={() => setShowAddBookmarkModal(false)}
                    formTitle="Add a bookmark"
                    fields={addBookmarkFormFields}
                    onSubmit={submitAddForm}
                    message={addBookmarkMessage}
                />

                <ModalForm
                    isOpen={showPasswordModal}
                    onClose={() => setShowPasswordModal(false)}
                    formTitle="This bookmark is password-locked"
                    fields={passwordFormFields}
                    onSubmit={submitPasswordForm}
                    message={passwordMessage}
                />

                <ModalShare
                    isOpen={showShareModal}
                    onClose={() => setShowShareModal(false)}
                    url={window.location.href}
                    collectionUuid={collectionUuid}
                    readonly={false}
                    readonlyEnabled={readonlyEnabled}
                />

                <ModalImport
                    isOpen={showImportModal}
                    onClose={() => setShowImportModal(false)}
                    collectionUuid={collectionUuid}
                />

                {!hasAccess && (
                    <div className="flex items-center justify-center min-h-[60vh]">
                        <div className="text-center">
                            <div className="text-6xl mb-4">🔒</div>
                            <p className="text-2xl text-gray-400 font-medium">No bookmarks to show</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default BookmarkCollection;
